/**
 * Home con look Quantia:
 *  - HeroSection oscuro con CTAs a las dos funcionalidades.
 *  - SectionHeader + cards Quantia (Analizador / Generador, una featured).
 *  - "Cómo empezar" como grid de 4 StatCards con número grande morado.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Button,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  CalendarMonth,
  Assessment,
  Gavel,
  AccountBalance,
  SmartToy,
  Speed,
} from '@mui/icons-material';

import {
  HeroSection,
  SectionHeader,
  QuantiaCard,
  StatCard,
  Badge,
} from '../components/quantia';
import { COLORS } from '../theme/theme';

const steps = [
  { num: '01', label: 'Analiza',  desc: 'Procesa los pliegos con el Analizador IA.' },
  { num: '02', label: 'Decide',   desc: 'Compara plazos, criterios y solvencia.' },
  { num: '03', label: 'Genera',   desc: 'Crea borradores con el Generador de Ofertas.' },
  { num: '04', label: 'Revisa',   desc: 'Ajusta y personaliza antes de presentarlo.' },
];

const Home = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, md: 3 }, pb: 8 }}>
      {/* HERO ─────────────────────────────────────────── */}
      <Box sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 5, md: 8 } }}>
        <HeroSection
          badge="IA aplicada a licitaciones"
          title={
            <>
              Tu asistente para licitaciones,<br />
              <em>potenciado por IA.</em>
            </>
          }
          body="Analiza pliegos en minutos, identifica plazos y criterios clave, y genera borradores de oferta listos para revisión, todo con la fuerza de la inteligencia artificial."
          actions={
            <>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => navigate('/analizar')}
                endIcon={<ArrowForwardIcon />}
              >
                Analizar una licitación
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/generar')}
                sx={{
                  color: '#FFFFFF',
                  borderColor: 'rgba(255,255,255,0.3)',
                  background: 'transparent',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.7)',
                    background: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Generar oferta
              </Button>
            </>
          }
        />
      </Box>

      {/* SERVICIOS ─────────────────────────────────────── */}
      <Box sx={{ pb: { xs: 5, md: 8 } }}>
        <SectionHeader
          label="01 — Servicios"
          title="Funcionalidades principales"
          highlight="principales"
          description="Dos herramientas integradas que cubren el ciclo completo: del análisis del pliego al borrador de oferta listo para revisar."
        />

        <Grid container spacing={3}>
          {/* Analizador */}
          <Grid item xs={12} md={6}>
            <QuantiaCard
              icon={<SearchIcon sx={{ color: COLORS.primary }} />}
              iconTone="purple"
              tag="Analizador · IA"
              title="Analizador de Licitaciones"
              onClick={() => navigate('/analizar')}
            >
              <Typography variant="body2" sx={{ color: COLORS.gray, mb: 2 }}>
                Extrae automáticamente la información clave de pliegos en PDF.
              </Typography>

              <List dense disablePadding>
                <ListItem disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CalendarMonth sx={{ color: COLORS.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 13, color: COLORS.gray }}
                    primary="Plazos y fechas clave"
                    secondary="Fechas límite y aperturas"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Assessment sx={{ color: COLORS.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 13, color: COLORS.gray }}
                    primary="Criterios de valoración"
                    secondary="Automáticos y discrecionales"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Gavel sx={{ color: COLORS.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 13, color: COLORS.gray }}
                    primary="Requisitos de solvencia"
                    secondary="Económicos, técnicos y profesionales"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <AccountBalance sx={{ color: COLORS.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 13, color: COLORS.gray }}
                    primary="Valor estimado y más"
                    secondary="Hasta 18 campos extraídos"
                  />
                </ListItem>
              </List>

              <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
                <Badge variant="purple" icon={<SmartToy sx={{ fontSize: 12 }} />}>
                  OpenAI · RAG
                </Badge>
                <Badge variant="lime" icon={<Speed sx={{ fontSize: 12 }} />}>
                  Excel exportable
                </Badge>
              </Stack>

              <Box sx={{ mt: 'auto', pt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  fullWidth
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/analizar');
                  }}
                >
                  Ir al Analizador
                </Button>
              </Box>
            </QuantiaCard>
          </Grid>

          {/* Generador (variante featured) */}
          <Grid item xs={12} md={6}>
            <QuantiaCard
              variant="featured"
              icon={<DescriptionIcon />}
              iconTone="lime"
              tag="Destacado"
              title="Generador de Ofertas"
              onClick={() => navigate('/generar')}
            >
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.78)', mb: 2 }}
              >
                Crea borradores profesionales con tus referencias y GPT-4.
              </Typography>

              <List dense disablePadding>
                {[
                  ['Usa tus documentos de referencia', 'Ofertas anteriores como base'],
                  ['Búsqueda inteligente con RAG', 'Encuentra propuestas relevantes'],
                  ['Contenido coherente con IA', 'OpenAI GPT-4 redacta secciones'],
                  ['Estructura profesional', 'Técnica + Económica'],
                ].map(([primary, secondary]) => (
                  <ListItem key={primary} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: COLORS.accent, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={primary}
                      secondary={secondary}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#FFFFFF',
                      }}
                      secondaryTypographyProps={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.6)',
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
                <Badge variant="dark" icon={<SmartToy sx={{ fontSize: 12 }} />}>
                  GPT-4 · RAG
                </Badge>
                <Badge variant="dark" icon={<Speed sx={{ fontSize: 12 }} />}>
                  TXT · Markdown
                </Badge>
              </Stack>

              <Box sx={{ mt: 'auto', pt: 3 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  endIcon={<ArrowForwardIcon />}
                  fullWidth
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/generar');
                  }}
                >
                  Ir al Generador
                </Button>
              </Box>
            </QuantiaCard>
          </Grid>
        </Grid>
      </Box>

      {/* CÓMO EMPEZAR ──────────────────────────────────── */}
      <Box>
        <SectionHeader
          label="02 — Flujo de trabajo"
          title="Cómo empezar"
          highlight="empezar"
          description="Cuatro pasos para pasar de un pliego complejo a una oferta lista para revisión."
        />

        <Grid container spacing={2}>
          {steps.map((step) => (
            <Grid item xs={6} md={3} key={step.num}>
              <StatCard
                label={`Paso ${step.num}`}
                value={step.label}
                trend={step.desc}
              />
            </Grid>
          ))}
        </Grid>

        <Typography
          variant="body2"
          sx={{
            mt: 5,
            textAlign: 'center',
            color: COLORS.gray,
            fontStyle: 'italic',
          }}
        >
          QUANTIA · Licitator — transformando la gestión de licitaciones con IA.
        </Typography>
      </Box>
    </Box>
  );
};

export default Home;
